// Copyright 2024 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React from "react";
import {Button, Checkbox, Form, Input, Modal} from "antd";
import {LockOutlined, UserOutlined} from "@ant-design/icons";
import i18next from "i18next";
import * as Provider from "./Provider";
import * as Setting from "../Setting";
import {StaticBaseUrl} from "../Setting";

class MobileLoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showPasswordForm: false,
      agreedToTerms: false,
      showAgreementModal: false,
      pendingLoginAction: null,
    };
    this.form = React.createRef();
  }

  getWeChatProvider() {
    const {application} = this.props;
    if (!application || !application.providers) {
      return null;
    }
    return application.providers.find(providerItem =>
      providerItem.provider && providerItem.provider.type === "WeChat"
    );
  }

  getAppleProvider() {
    const {application} = this.props;
    if (!application || !application.providers) {
      return null;
    }
    return application.providers.find(providerItem =>
      providerItem.provider && providerItem.provider.type === "Apple"
    );
  }

  openExternalLink(url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }

  checkAgreementAndProceed(action) {
    if (this.state.agreedToTerms) {
      action();
    } else {
      this.setState({
        showAgreementModal: true,
        pendingLoginAction: action,
      });
    }
  }

  handleWeChatLogin() {
    const action = () => {
      const providerItem = this.getWeChatProvider();
      if (providerItem) {
        const provider = providerItem.provider;
        const application = this.props.application;

        // Handle WeChat official account login (scan QR code)
        if (provider.clientId2 !== "" && provider.clientSecret2 !== "" && provider.disableSsl === true && !navigator.userAgent.includes("MicroMessenger")) {
          // Import and use WechatOfficialAccountModal
          import("./Util").then(Util => {
            Util.WechatOfficialAccountModal(application, provider, "signup");
          });
        } else {
          // Regular WeChat OAuth login
          window.location.href = Provider.getAuthUrl(application, provider, "signup");
        }
      }
    };

    this.checkAgreementAndProceed(action);
  }

  handleAppleLogin() {
    const action = () => {
      const providerItem = this.getAppleProvider();
      if (providerItem) {
        window.location.href = Provider.getAuthUrl(this.props.application, providerItem.provider, "signup");
      }
    };

    this.checkAgreementAndProceed(action);
  }

  handlePasswordLogin() {
    this.checkAgreementAndProceed(() => {
      this.setState({showPasswordForm: true});
    });
  }

  handlePasswordLoginSubmit(values) {
    // Set login method to password
    values["loginMethod"] = "password";
    if (this.props.onFinish) {
      this.props.onFinish(values);
    }
  }

  handleBackToLoginOptions() {
    this.setState({showPasswordForm: false});
  }

  handleAgreementModalOk() {
    this.setState({
      agreedToTerms: true,
      showAgreementModal: false,
    });
    // Execute pending login action if exists
    if (this.state.pendingLoginAction) {
      this.state.pendingLoginAction();
      this.setState({pendingLoginAction: null});
    }
  }

  handleAgreementModalCancel() {
    this.setState({
      showAgreementModal: false,
      showPasswordForm: false, // Go back to login options
      pendingLoginAction: null,
    });
  }

  renderAgreementModal() {
    return (
      <Modal
        title={i18next.t("login:User Agreement and Privacy Policy")}
        open={this.state.showAgreementModal}
        onOk={() => this.handleAgreementModalOk()}
        onCancel={() => this.handleAgreementModalCancel()}
        okText={i18next.t("login:Agree")}
        cancelText={i18next.t("login:Cancel")}
        centered
        width={320}
      >
        <div style={{lineHeight: "1.8"}}>
          {i18next.t("login:I have read and agree to")}&nbsp;
          <a
            onClick={() => this.openExternalLink("https://www.markai.com.cn/terms-of-service")}
            style={{color: "#000000", cursor: "pointer"}}
          >
            {i18next.t("login:User Agreement")}
          </a>
          &nbsp;{i18next.t("login:and")}&nbsp;
          <a
            onClick={() => this.openExternalLink("https://www.markai.com.cn/privacy-policy")}
            style={{color: "#000000", cursor: "pointer"}}
          >
            {i18next.t("login:Privacy Policy")}
          </a>
          .
        </div>
      </Modal>
    );
  }

  renderPasswordLoginForm() {
    const {application} = this.props;
    return (
      <React.Fragment>
        {/* Top section with Logo, Name and Form */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}>
          {/* Logo */}
          <div>
            <img
              src="https://www.markai.com.cn/favicon.ico"
              alt="米开写作"
              style={{maxWidth: "200px", maxHeight: "80px"}}
            />
          </div>

          {/* Application Name */}
          <div style={{
            fontSize: "24px",
            fontWeight: "bold",
            fontFamily: "SimHei, 'Heiti SC', 'Microsoft YaHei', sans-serif",
            color: "#0067D2",
            marginBottom: "60px",
          }}>
            米开写作
          </div>

          {/* Login Form */}
          <div style={{width: "100%", maxWidth: "320px"}}>
            <Form
              ref={this.form}
              name="mobile_login"
              initialValues={{
                organization: application?.organization,
                application: application?.name,
                autoSignin: !application?.signinItems?.map(signinItem => signinItem.name === "Forgot password?" && signinItem.rule === "Auto sign in - False")?.includes(true),
              }}
              onFinish={(values) => this.handlePasswordLoginSubmit(values)}
            >
              <Form.Item
                hidden={true}
                name="application"
              >
                <Input />
              </Form.Item>
              <Form.Item
                hidden={true}
                name="organization"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="username"
                rules={[{required: true, message: i18next.t("login:Please input your username!")}]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={i18next.t("login:username, Email or phone")}
                  size="large"
                  style={{borderRadius: "8px"}}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{required: true, message: i18next.t("login:Please input your password!")}]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder={i18next.t("login:Password")}
                  size="large"
                  style={{borderRadius: "8px"}}
                />
              </Form.Item>
              <Form.Item name="autoSignin" valuePropName="checked" noStyle>
                <input type="hidden" />
              </Form.Item>
              {/* Forgot Password Link */}
              <Form.Item style={{marginBottom: "8px"}}>
                <div style={{textAlign: "right"}}>
                  {Setting.renderForgetLink(application, i18next.t("login:Forgot password?"))}
                </div>
              </Form.Item>
              <Form.Item style={{marginBottom: "12px"}}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={this.props.loginLoading}
                  style={{borderRadius: "8px", height: "50px", fontSize: "16px", fontWeight: "500"}}
                >
                  {i18next.t("login:Sign In")}
                </Button>
              </Form.Item>
              {/* Signup Link */}
              <Form.Item style={{marginBottom: "0"}}>
                <div style={{textAlign: "right"}}>
                  {i18next.t("login:No account?")}&nbsp;
                  {Setting.renderSignupLink(application, i18next.t("login:sign up now"))}
                </div>
              </Form.Item>
            </Form>
          </div>
        </div>

        {/* Bottom section with back button */}
        <div style={{
          width: "100%",
          maxWidth: "320px",
          marginBottom: "40px",
        }}>
          <Button
            type="default"
            size="large"
            block
            onClick={() => this.handleBackToLoginOptions()}
            style={{borderRadius: "8px", height: "50px", fontSize: "16px"}}
          >
            {i18next.t("login:Other login methods")}
          </Button>
        </div>
      </React.Fragment>
    );
  }

  renderLoginButton(type, onClick, bgColor, textColor, iconSrc, text) {
    const buttonStyle = {
      width: "100%",
      height: "50px",
      backgroundColor: bgColor,
      color: textColor,
      borderRadius: "8px",
      border: bgColor === "#ffffff" ? "1px solid #d9d9d9" : "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "12px",
      cursor: "pointer",
      fontSize: "16px",
      fontWeight: "500",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    };

    const iconStyle = {
      width: "24px",
      height: "24px",
      marginRight: "8px",
    };

    // For password button, use LockOutlined icon
    const icon = type === "password" ? (
      <LockOutlined style={{fontSize: "24px", marginRight: "8px"}} />
    ) : (
      <img src={iconSrc} alt={text} style={iconStyle} />
    );

    return (
      <div
        key={type}
        style={buttonStyle}
        onClick={onClick}
      >
        {icon}
        <span>{text}</span>
      </div>
    );
  }

  render() {
    const {application} = this.props;
    const wechatProvider = this.getWeChatProvider();
    const appleProvider = this.getAppleProvider();
    const hasPasswordLogin = Setting.isPasswordEnabled(application);

    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "20px",
        backgroundColor: "#f5f5f5",
        overflow: "auto",
      }}>
        {/* Conditional rendering: Password Form or Login Selection */}
        {this.state.showPasswordForm ? (
          this.renderPasswordLoginForm()
        ) : (
          <React.Fragment>
            {/* Top section with Logo and Name */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              width: "100%",
            }}>
              {/* Logo */}
              <div>
                <img
                  src="https://www.markai.com.cn/favicon.ico"
                  alt="米开写作"
                  style={{maxWidth: "200px", maxHeight: "80px"}}
                />
              </div>

              {/* Application Name */}
              <div style={{
                fontSize: "24px",
                fontWeight: "bold",
                fontFamily: "SimHei, 'Heiti SC', 'Microsoft YaHei', sans-serif",
                color: "#0067D2",
              }}>
                米开写作
              </div>
            </div>

            {/* Bottom section with Login Buttons and Agreement */}
            <div style={{width: "100%", maxWidth: "320px", marginBottom: "40px"}}>
              {/* Login Buttons */}
              {/* WeChat Login Button */}
              {wechatProvider && this.renderLoginButton(
                "wechat",
                () => this.handleWeChatLogin(),
                "#07C160",
                "#ffffff",
                `${StaticBaseUrl}/buttons/wechat.svg`,
                i18next.t("login:Sign in with {type}").replace("{type}", i18next.t("login:WeChat"))
              )}

              {/* Apple Login Button */}
              {appleProvider && this.renderLoginButton(
                "apple",
                () => this.handleAppleLogin(),
                "#ffffff",
                "#000000",
                `${StaticBaseUrl}/buttons/apple.svg`,
                i18next.t("login:Sign in with {type}").replace("{type}", "Apple")
              )}

              {/* Password Login Button */}
              {hasPasswordLogin && this.renderLoginButton(
                "password",
                () => this.handlePasswordLogin(),
                "#ffffff",
                "#000000",
                null,
                i18next.t("login:Sign in with {type}").replace("{type}", i18next.t("login:Password"))
              )}

              {/* Agreement Checkbox */}
              <div style={{
                textAlign: "center",
                marginTop: "16px",
                fontSize: "11px",
                color: "#666666",
              }}>
                <Checkbox
                  checked={this.state.agreedToTerms}
                  onChange={(e) => this.setState({agreedToTerms: e.target.checked})}
                  style={{fontSize: "11px"}}
                >
                  {i18next.t("login:I have read and agree to")}&nbsp;
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      this.openExternalLink("https://www.markai.com.cn/terms-of-service");
                    }}
                    style={{color: "#000000", cursor: "pointer"}}
                  >
                    {i18next.t("login:User Agreement")}
                  </a>
                  &nbsp;{i18next.t("login:and")}&nbsp;
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      this.openExternalLink("https://www.markai.com.cn/privacy-policy");
                    }}
                    style={{color: "#000000", cursor: "pointer"}}
                  >
                    {i18next.t("login:Privacy Policy")}
                  </a>
                </Checkbox>
              </div>
            </div>
          </React.Fragment>
        )}

        {/* Agreement Modal */}
        {this.renderAgreementModal()}
      </div>
    );
  }
}

export default MobileLoginPage;
